import type { TypedSupabaseClient } from '../client';
import type { TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  IPurchaseRepository,
  PurchaseDocument,
  PurchaseDocumentLine,
  PurchaseDocumentFilters,
  CreatePurchaseDocumentInput,
  UpdatePurchaseDocumentInput,
} from '../../../interfaces/src';

export class PurchaseNumberConflictError extends Error {
  readonly code = 'PURCHASE_NUMBER_CONFLICT';
  constructor(message = 'Purchase document number already used for this supplier') {
    super(message);
    this.name = 'PurchaseNumberConflictError';
  }
}

function isUniqueViolation(err: unknown, indexName: string): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: string; message?: string };
  return e.code === '23505' && typeof e.message === 'string' && e.message.includes(indexName);
}

export class SupabasePurchaseRepository implements IPurchaseRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async listDocuments(filters?: PurchaseDocumentFilters): Promise<PurchaseDocument[]> {
    let query = this.supabase.from('purchase_documents').select('*, purchase_document_lines(*)');
    query = applyPurchaseDocumentFilters(query, filters);
    query = query.order('created_at', { ascending: false });
    if (filters?.limit && filters.limit > 0) {
      const offset = Math.max(0, filters.offset ?? 0);
      query = query.range(offset, offset + filters.limit - 1);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map((row) => mapPurchaseDocument(row));
  }

  async countDocuments(filters?: PurchaseDocumentFilters): Promise<number> {
    let query = this.supabase
      .from('purchase_documents')
      .select('id', { count: 'exact', head: true });
    query = applyPurchaseDocumentFilters(query, filters);
    const { count, error } = await query;
    if (error) throw error;
    return count ?? 0;
  }

  async getDocumentById(id: string): Promise<PurchaseDocument | null> {
    const { data, error } = await this.supabase
      .from('purchase_documents')
      .select('*, purchase_document_lines(*)')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapPurchaseDocument(data);
  }

  async createDocument(input: CreatePurchaseDocumentInput): Promise<PurchaseDocument> {
    const { data: doc, error: docError } = await this.supabase
      .from('purchase_documents')
      .insert({
        doc_type: input.docType,
        number: input.number,
        supplier_id: input.supplierId,
        doc_date: input.docDate,
        parent_doc_id: input.parentDocId,
        parent_doc_ids: input.parentDocIds ?? (input.parentDocId ? [input.parentDocId] : []),
        notes: input.notes,
        total_ht: input.totalHt ?? 0,
        total_vat: input.totalVat ?? 0,
        total_ttc: input.totalTtc ?? 0,
        withholding_amount: input.withholdingAmount ?? 0,
        stamp_amount: input.stampAmount ?? 0,
        net_to_pay: input.netToPay ?? 0,
      })
      .select()
      .single();
    if (docError) {
      if (isUniqueViolation(docError, 'uq_purchase_docs_supplier_number')) {
        throw new PurchaseNumberConflictError();
      }
      throw docError;
    }

    if (input.lines.length > 0) {
      const lines = input.lines.map((line, idx) => ({
        document_id: doc.id,
        line_number: idx + 1,
        article_id: line.articleId,
        description: line.description,
        quantity: line.quantity,
        unit_price_ht: line.unitPriceHt,
        purchase_price: line.unitPriceHt ?? 0,
        discount_pct: line.discountPct ?? 0,
        vat_rate: line.vatRate,
        total_ht: line.totalHt ?? 0,
        total_ttc: line.totalTtc ?? 0,
      }));

      const { error: linesError } = await this.supabase.from('purchase_document_lines').insert(lines);
      if (linesError) throw linesError;
    }

    return (await this.getDocumentById(doc.id))!;
  }

  async updateDocument(id: string, input: UpdatePurchaseDocumentInput): Promise<PurchaseDocument> {
    const mapped: TablesUpdate<'purchase_documents'> = {};
    if (input.number !== undefined) mapped['number'] = input.number;
    if (input.supplierId !== undefined) mapped['supplier_id'] = input.supplierId;
    if (input.docDate !== undefined) mapped['doc_date'] = input.docDate;
    if (input.notes !== undefined) mapped['notes'] = input.notes;
    if (input.totalHt !== undefined) mapped['total_ht'] = input.totalHt;
    if (input.totalVat !== undefined) mapped['total_vat'] = input.totalVat;
    if (input.totalTtc !== undefined) mapped['total_ttc'] = input.totalTtc;
    if (input.withholdingAmount !== undefined) mapped['withholding_amount'] = input.withholdingAmount;
    if (input.stampAmount !== undefined) mapped['stamp_amount'] = input.stampAmount;
    if (input.netToPay !== undefined) mapped['net_to_pay'] = input.netToPay;

    if (Object.keys(mapped).length > 0) {
      const { error } = await this.supabase.from('purchase_documents').update(mapped).eq('id', id);
      if (error) {
        if (isUniqueViolation(error, 'uq_purchase_docs_supplier_number')) {
          throw new PurchaseNumberConflictError();
        }
        throw error;
      }
    }

    // Replace lines if provided
    if (input.lines) {
      // Delete old lines
      const { error: delError } = await this.supabase
        .from('purchase_document_lines')
        .delete()
        .eq('document_id', id);
      if (delError) throw delError;

      // Insert new lines
      if (input.lines.length > 0) {
        const lines = input.lines.map((line, idx) => ({
          document_id: id,
          line_number: idx + 1,
          article_id: line.articleId,
          description: line.description,
          quantity: line.quantity,
          unit_price_ht: line.unitPriceHt,
          purchase_price: line.unitPriceHt ?? 0,
          discount_pct: line.discountPct ?? 0,
          vat_rate: line.vatRate,
          total_ht: line.totalHt ?? 0,
          total_ttc: line.totalTtc ?? 0,
        }));
        const { error: insError } = await this.supabase.from('purchase_document_lines').insert(lines);
        if (insError) throw insError;
      }
    }

    return (await this.getDocumentById(id))!;
  }

  async deleteDocument(id: string): Promise<void> {
    const { data: doc, error: fetchErr } = await this.supabase
      .from('purchase_documents')
      .select('id, doc_type, parent_doc_id, parent_doc_ids')
      .eq('id', id)
      .single();
    if (fetchErr) throw fetchErr;

    const parentIds: string[] = [];
    if (doc?.['doc_type'] === 'invoice') {
      const single = doc['parent_doc_id'] as string | null;
      const many = (doc['parent_doc_ids'] as string[] | null) ?? [];
      if (single) parentIds.push(single);
      for (const pid of many) if (pid && !parentIds.includes(pid)) parentIds.push(pid);
    }

    const { error: delErr } = await this.supabase.from('purchase_documents').delete().eq('id', id);
    if (delErr) throw delErr;

    for (const pid of parentIds) {
      const { data: stillReferenced } = await this.supabase
        .from('purchase_documents')
        .select('id')
        .eq('doc_type', 'invoice')
        .or(`parent_doc_id.eq.${pid},parent_doc_ids.cs.{${pid}}`)
        .limit(1);
      if (!stillReferenced || stillReferenced.length === 0) {
        await this.supabase
          .from('purchase_documents')
          .update({ is_invoiced: false })
          .eq('id', pid);
      }
    }
  }

  async markAsInvoiced(id: string): Promise<void> {
    const { error } = await this.supabase
      .from('purchase_documents')
      .update({ is_invoiced: true })
      .eq('id', id);
    if (error) throw error;
  }

  async getChildDocuments(parentId: string): Promise<PurchaseDocument[]> {
    const { data, error } = await this.supabase
      .from('purchase_documents')
      .select('*, purchase_document_lines(*)')
      .contains('parent_doc_ids', [parentId]);
    if (error) throw error;
    return (data ?? []).map(mapPurchaseDocument);
  }
}

function escapeLike(value: string): string {
  return value.replace(/[%_]/g, '\\$&');
}

function applyPurchaseDocumentFilters(query: any, filters?: PurchaseDocumentFilters): any {
  if (filters?.docType) query = query.eq('doc_type', filters.docType);
  if (filters?.search?.trim()) query = query.ilike('number', `%${escapeLike(filters.search.trim())}%`);
  if (filters?.supplierId) query = query.eq('supplier_id', filters.supplierId);
  if (filters?.dateFrom) query = query.gte('doc_date', filters.dateFrom);
  if (filters?.dateTo) query = query.lte('doc_date', filters.dateTo);
  if (filters?.isInvoiced !== undefined) query = query.eq('is_invoiced', filters.isInvoiced);
  return query;
}

function mapPurchaseDocument(row: Record<string, unknown>): PurchaseDocument {
  const lines = (row['purchase_document_lines'] as Record<string, unknown>[] | undefined) ?? [];
  return {
    id: row['id'] as string,
    docType: row['doc_type'] as PurchaseDocument['docType'],
    number: row['number'] as string,
    supplierId: row['supplier_id'] as string,
    docDate: row['doc_date'] as string,
    parentDocId: (row['parent_doc_id'] as string) ?? null,
    parentDocIds: (row['parent_doc_ids'] as string[]) ?? [],
    isInvoiced: (row['is_invoiced'] as boolean) ?? false,
    totalHt: (row['total_ht'] as number) ?? 0,
    totalVat: (row['total_vat'] as number) ?? 0,
    totalTtc: (row['total_ttc'] as number) ?? 0,
    withholdingAmount: (row['withholding_amount'] as number) ?? 0,
    stampAmount: (row['stamp_amount'] as number) ?? 0,
    netToPay: (row['net_to_pay'] as number) ?? 0,
    notes: (row['notes'] as string) ?? null,
    createdBy: (row['created_by'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    lines: lines.map(mapPurchaseLine),
  };
}

function mapPurchaseLine(row: Record<string, unknown>): PurchaseDocumentLine {
  return {
    id: row['id'] as string,
    documentId: row['document_id'] as string,
    lineNumber: row['line_number'] as number,
    articleId: (row['article_id'] as string) ?? null,
    description: row['description'] as string,
    quantity: row['quantity'] as number,
    unitPriceHt: row['unit_price_ht'] as number,
    purchasePrice: (row['purchase_price'] as number) ?? 0,
    discountPct: (row['discount_pct'] as number) ?? 0,
    vatRate: row['vat_rate'] as number,
    totalHt: row['total_ht'] as number,
    totalTtc: row['total_ttc'] as number,
  };
}
